/**
 * Stufe 1 ARKO_TASK_NOT_DROPPED im Portal.
 * Die erste Aufgabe geht an ARKO. Die zweite bleibt ein Chip und steht nicht in derselben Blase.
 * Eine einzelne Rechtsfrage bleibt ungeteilt.
 */
import type { SuggestedAction } from './portalArkoChipSink';

export interface DeferredPortalTask {
  action: 'web_recherche' | 'schreiben_erstellen';
  label: string;
  query: string;
}

export interface PortalTaskSplit {
  requestText: string;
  deferred: DeferredPortalTask | null;
}

const WEB_TASK_RE =
  /\b((?:im|ins)\s+(?:internet|web)|online\s+recherch\w*|web\s*suche|internetsuche|googlen)\b/i;
const SCHREIBEN_VERB_RE =
  /\b(formulier|verfass|entwerf|entwirf|entwurf|aufsetz|schreib|erstell|generier|mach\s+mir)/i;
const SCHREIBEN_NOUN_RE =
  /\b(schreiben|schriftsatz|schriftsaetze|anschreiben|begleitschreiben|brief|mahnung|mahnschreiben|stellungnahme|aufforderungsschreiben|e-?mail(?:vorlage|entwurf)?)\b/i;

function firstIndex(re: RegExp, text: string): number {
  const flags = re.flags.includes('g') ? re.flags : `${re.flags}g`;
  const copy = new RegExp(re.source, flags);
  const match = copy.exec(text);
  return match ? match.index : -1;
}

function clauseCut(text: string, marker: number): number {
  const before = text.slice(0, marker);
  const lower = before.toLowerCase();
  const und = lower.lastIndexOf(' und ');
  const sowie = lower.lastIndexOf(' sowie ');
  const dot = before.lastIndexOf('. ');
  const question = before.lastIndexOf('? ');
  const cut = Math.max(und, sowie, dot, question);
  if (cut < 0) return marker;
  if (cut === und) return und + ' und '.length;
  if (cut === sowie) return sowie + ' sowie '.length;
  return cut + 2;
}

export function splitPortalTasks(text: string): PortalTaskSplit {
  const original = String(text || '').trim();
  const raw = original.replace(/\s+/g, ' ').trim();
  if (!raw) return { requestText: '', deferred: null };
  const tasks: Array<{ action: DeferredPortalTask['action']; label: string; index: number }> = [];
  const webIdx = firstIndex(WEB_TASK_RE, raw);
  if (webIdx >= 0) {
    tasks.push({ action: 'web_recherche', label: 'Im Web suchen', index: webIdx });
  }
  if (SCHREIBEN_VERB_RE.test(raw) && SCHREIBEN_NOUN_RE.test(raw)) {
    const verb = firstIndex(SCHREIBEN_VERB_RE, raw);
    const noun = firstIndex(SCHREIBEN_NOUN_RE, raw);
    tasks.push({
      action: 'schreiben_erstellen',
      label: 'Schreiben erstellen',
      index: Math.min(verb, noun),
    });
  }
  tasks.sort((a, b) => a.index - b.index);
  if (tasks.length < 2) return { requestText: original, deferred: null };
  const second = tasks[1];
  const start = clauseCut(raw, second.index);
  const requestText = raw.slice(0, start).replace(/[\s,;:.]+$/g, '').trim();
  const query = raw.slice(start).trim();
  if (!requestText || !query) return { requestText: raw, deferred: null };
  return {
    requestText,
    deferred: {
      action: second.action,
      label: second.label,
      query: query.slice(0, 500),
    },
  };
}

/** Zweite Überschrift nur streichen, wenn wirklich eine zweite Aufgabe offen ist. */
export function dropDeferredAnswerSection(reply: string, deferred: boolean): string {
  const text = String(reply || '');
  if (!deferred || !text.trim()) return text;
  const re = /\n+\*\*[^*\n]*(?:Entwicklung|September|Internet|Auflistung|Websuche|im Web)[^*\n]*\*\*/i;
  const match = re.exec(text);
  if (!match || match.index < 40) return text;
  return text.slice(0, match.index).trim();
}

export function mergeDeferredChip(
  actions: SuggestedAction[],
  deferred: DeferredPortalTask | null,
): SuggestedAction[] {
  if (!deferred) return actions;
  const rest = actions.filter((action) => action.action !== deferred.action);
  return [
    ...rest,
    {
      action: deferred.action,
      label: deferred.label,
      followupQuery: deferred.query,
    },
  ];
}
